import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import type { WordBloomLevel } from './types';
import './wordBloom.css';

const MIN_LEN = 4;
const MAX_LEN = 8;

export default function WordBloomGame() {
  const [data, setData] = useState<{
    level: WordBloomLevel;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<WordBloomLevel>('wordBloom')
      .then((res) => {
        if (cancelled) return;
        if (!res) setLoadError('No daily puzzle published for Word Bloom yet.');
        else setData({ level: res.data, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="wordBloom" />;
  if (!data) {
    return (
      <GameShell gameId="wordBloom">
        <LoadingScreen label="Fetching today's bloom…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('wordBloom', data.dateKey)) {
    return (
      <FinishedDaily gameId="wordBloom" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <WordBloomPlay
      key={data.dateKey}
      level={data.level}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

function WordBloomPlay({
  level,
  dateKey,
  isToday,
}: {
  level: WordBloomLevel;
  dateKey: string;
  isToday: boolean;
}) {
  const center = level.centerLetter.toUpperCase();
  const petals = useMemo(() => level.petalLetters.map((l) => l.toUpperCase()), [level]);
  const validWords = useMemo(
    () => new Set(level.validWords.map((w) => w.toUpperCase())),
    [level],
  );

  const [currentWord, setCurrentWord] = useState('');
  const { restored, save } = useDailySnapshot<{ found: string[]; hintUsed: boolean }>(
    'wordBloom',
    dateKey,
  );

  const [found, setFound] = useState<Set<string>>(() => new Set(restored?.found ?? []));
  const [petalOrder, setPetalOrder] = useState<number[]>(() => petals.map((_, i) => i));
  const [toast, setToast] = useState<{ msg: string; style: 'success' | 'error' | 'info' } | null>(null);
  const [shake, setShake] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(restored?.hintUsed ?? false);

  usePersistedState(save, { found: [...found], hintUsed }, !completed);

  useEffect(() => {
    if (completed) recordOutcome('wordBloom', dateKey, 'won', { subtitle: 'Every word bloomed.' });
  }, [completed, dateKey]);

  const showToast = useCallback((msg: string, style: 'success' | 'error' | 'info') => {
    setToast({ msg, style });
    setTimeout(() => setToast(null), 1400);
  }, []);

  const doShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  const tapLetter = useCallback(
    (letter: string) => {
      if (completed) return;
      setCurrentWord((w) => (w.length < MAX_LEN ? w + letter : w));
    },
    [completed],
  );

  const deleteLast = useCallback(() => {
    setCurrentWord((w) => w.slice(0, -1));
  }, []);

  const clearWord = useCallback(() => setCurrentWord(''), []);

  const shufflePetals = useCallback(() => {
    setPetalOrder((prev) => {
      const a = [...prev];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    });
  }, []);

  const useHint = useCallback(() => {
    if (completed || hintUsed) return;
    const unfound = [...validWords].filter((w) => !found.has(w));
    if (unfound.length === 0) {
      showToast('All words found!', 'success');
      return;
    }
    // Mirror iOS: prefer the shortest unfound word so a hint is a small unlock,
    // not a freebie on the rarest 8-letter answer.
    unfound.sort((a, b) => a.length - b.length);
    const word = unfound[0];
    setHintUsed(true);
    setFound((prev) => {
      const next = new Set(prev).add(word);
      if (next.size === validWords.size) setCompleted(true);
      return next;
    });
    showToast(`Revealed: ${word}`, 'success');
  }, [completed, hintUsed, validWords, found, showToast]);

  const submitWord = useCallback(() => {
    if (completed) return;
    const word = currentWord.toUpperCase();

    if (word.length < MIN_LEN) {
      showToast(`Too short — ${MIN_LEN} letters minimum`, 'error');
      doShake();
      return;
    }
    if (word.length > MAX_LEN) {
      showToast(`Too long — ${MAX_LEN} letters maximum`, 'error');
      doShake();
      return;
    }
    if (!word.includes(center)) {
      showToast('Must use the center letter', 'error');
      doShake();
      return;
    }
    const allowed = new Set([center, ...petals]);
    if (!word.split('').every((c) => allowed.has(c))) {
      showToast('Invalid letters used', 'error');
      doShake();
      return;
    }
    if (!validWords.has(word)) {
      showToast('Not in word list', 'error');
      doShake();
      return;
    }
    if (found.has(word)) {
      showToast('Already found!', 'info');
      doShake();
      return;
    }

    setFound((prev) => {
      const next = new Set(prev).add(word);
      if (next.size === validWords.size) setCompleted(true);
      return next;
    });
    showToast(
      word.length >= 7 ? `✨ ${word}` : word.length >= 5 ? `Great! ${word}` : `Good! ${word}`,
      'success',
    );
    setCurrentWord('');
  }, [completed, currentWord, center, petals, validWords, found, showToast]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (/^[a-zA-Z]$/.test(e.key)) {
        const letter = e.key.toUpperCase();
        const allowed = new Set([center, ...petals]);
        if (allowed.has(letter)) {
          e.preventDefault();
          tapLetter(letter);
        }
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        deleteLast();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        submitWord();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        clearWord();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [center, petals, tapLetter, deleteLast, submitWord, clearWord]);

  const groupedWords = useMemo(() => {
    const groups: Record<number, string[]> = {};
    for (const w of validWords) {
      (groups[w.length] ??= []).push(w);
    }
    return Object.entries(groups)
      .map(([len, words]) => ({ length: parseInt(len, 10), words: words.sort() }))
      .sort((a, b) => a.length - b.length);
  }, [validWords]);

  return (
    <GameShell
      gameId="wordBloom"
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${found.size}/${validWords.size} words`}
      actions={
        <button
          className="btn btn-secondary"
          onClick={useHint}
          disabled={completed || hintUsed}
          title="Reveal a short word"
        >
          💡 Hint
        </button>
      }
    >
      <div className="wb-board">
        <div className={'wb-current' + (shake ? ' is-shake' : '')} onClick={deleteLast}>
          {currentWord ? (
            currentWord.split('').map((ch, i) => (
              <span
                key={i}
                className={ch === center ? 'wb-current-char is-center' : 'wb-current-char'}
              >
                {ch}
              </span>
            ))
          ) : (
            <span className="wb-current-hint">Tap letters to spell</span>
          )}
        </div>

        <Bloom
          center={center}
          petals={petals}
          order={petalOrder}
          onLetter={tapLetter}
        />

        <div className="wb-actions">
          <button className="wb-action wb-action-ghost" onClick={deleteLast} aria-label="Delete">
            ⌫
          </button>
          <button className="wb-action wb-action-shuffle" onClick={shufflePetals} aria-label="Shuffle">
            ⇄
          </button>
          <button className="wb-action wb-action-primary" onClick={submitWord}>
            Enter
          </button>
        </div>

        <div className="wb-found-list">
          {groupedWords.map(({ length, words }) => (
            <div key={length} className="wb-group">
              <div className="wb-group-label">
                {length} letters · {words.filter((w) => found.has(w)).length}/{words.length}
              </div>
              <div className="wb-group-words">
                {words.map((w) => {
                  const isFound = found.has(w);
                  return (
                    <div
                      key={w}
                      className={'wb-word-row' + (isFound ? ' is-found' : '')}
                    >
                      {[...Array(w.length)].map((_, i) => (
                        <span
                          key={i}
                          className={'wb-word-slot' + (isFound ? ' is-found' : '')}
                        >
                          {isFound ? w[i] : ''}
                        </span>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {toast && <div className={`wb-toast wb-toast-${toast.style}`}>{toast.msg}</div>}

      {completed && (
        <DailyDoneOverlay
          dateKey={dateKey}
          isToday={isToday}
          subtitle={`Found all ${validWords.size} words.`}
          emoji="🌸"
        />
      )}
    </GameShell>
  );
}

function Bloom({
  center,
  petals,
  order,
  onLetter,
}: {
  center: string;
  petals: string[];
  order: number[];
  onLetter: (l: string) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(300);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setSize(Math.floor(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.3;
  const petalSize = size * 0.2;

  return (
    <div ref={wrapRef} className="wb-bloom">
      <svg className="wb-bloom-lines" viewBox={`0 0 ${size} ${size}`}>
        {order.map((_, i) => {
          const angle = (i * 60 - 90) * (Math.PI / 180);
          const x = cx + radius * Math.cos(angle);
          const y = cy + radius * Math.sin(angle);
          return (
            <line key={i} x1={cx} y1={cy} x2={x} y2={y} />
          );
        })}
      </svg>

      <button
        className="wb-letter wb-letter-center"
        style={{
          width: petalSize + 8,
          height: petalSize + 8,
          left: cx - (petalSize + 8) / 2,
          top: cy - (petalSize + 8) / 2,
          fontSize: Math.max(22, petalSize * 0.5),
        }}
        onClick={() => onLetter(center)}
      >
        {center}
      </button>

      {order.map((petalIdx, displayIdx) => {
        const angle = (displayIdx * 60 - 90) * (Math.PI / 180);
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        return (
          <button
            key={petalIdx}
            className="wb-letter wb-letter-petal"
            style={{
              width: petalSize,
              height: petalSize,
              left: x - petalSize / 2,
              top: y - petalSize / 2,
              fontSize: Math.max(20, petalSize * 0.45),
            }}
            onClick={() => onLetter(petals[petalIdx])}
          >
            {petals[petalIdx]}
          </button>
        );
      })}
    </div>
  );
}
